import type { ChatMessage } from "@loot-os/api-contract";
import { ArrowDown } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { ChatMessageRow } from "./ChatMessageRow.js";

/** Within this many pixels of the bottom, the list counts as pinned and follows new messages. */
const PINNED_PX = 40;

/**
 * The room's messages, oldest at the top. It stays pinned to the newest message while the reader
 * is at the bottom; scrolled up, it holds its place and offers a jump back down.
 */
export function ChatMessageList({
  messages,
  viewerEndUserId,
  viewerUsername,
  isIgnored,
  onReply,
  onReport,
  onDelete,
  onToggleIgnore,
}: {
  messages: ChatMessage[];
  viewerEndUserId: string | null;
  viewerUsername: string | null;
  isIgnored: (endUserId: string) => boolean;
  onReply: (message: ChatMessage) => void;
  onReport: (message: ChatMessage) => void;
  onDelete: (message: ChatMessage) => void;
  onToggleIgnore: (message: ChatMessage) => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);
  const [showJump, setShowJump] = useState(false);
  const [highlighted, setHighlighted] = useState<string | null>(null);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    const el = scroller.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    pinned.current = true;
    setShowJump(false);
  }, []);

  const lastId = messages[messages.length - 1]?.messageId;
  // biome-ignore lint/correctness/useExhaustiveDependencies: follows the newest message only
  useLayoutEffect(() => {
    if (pinned.current) scrollToBottom();
    else setShowJump(true);
  }, [lastId, scrollToBottom]);

  useEffect(() => {
    if (!highlighted) return;
    const timer = setTimeout(() => setHighlighted(null), 3000);
    return () => clearTimeout(timer);
  }, [highlighted]);

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= PINNED_PX;
    pinned.current = atBottom;
    if (atBottom) setShowJump(false);
  };

  const jumpTo = useCallback((messageId: string) => {
    const target = document.getElementById(`chat-message-${messageId}`);
    if (!target) return;
    target.scrollIntoView({ block: "center", behavior: "smooth" });
    setHighlighted(messageId);
  }, []);

  const visible = messages.filter((m) => !isIgnored(m.endUserId));

  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={scroller}
        onScroll={onScroll}
        className="h-full overflow-y-auto"
        role="log"
        aria-live="polite"
        aria-label="Chat messages"
      >
        {visible.length === 0 ? (
          <p className="px-2 py-8 text-center text-sm text-muted-foreground">
            Welcome to chat. Say hello!
          </p>
        ) : (
          visible.map((message) => (
            <ChatMessageRow
              key={message.messageId}
              message={message}
              viewerEndUserId={viewerEndUserId}
              viewerUsername={viewerUsername}
              highlighted={highlighted === message.messageId}
              ignored={false}
              onReply={onReply}
              onReport={onReport}
              onDelete={onDelete}
              onToggleIgnore={onToggleIgnore}
              onJump={jumpTo}
            />
          ))
        )}
      </div>
      {showJump && (
        <button
          type="button"
          onClick={() => scrollToBottom("smooth")}
          className="absolute bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full border border-border bg-background px-3 py-1 text-xs shadow-md hover:bg-muted"
        >
          <ArrowDown className="size-3.5" /> New messages
        </button>
      )}
    </div>
  );
}
